import React, { useEffect, useRef, useState } from "react";
import {
  AbsoluteInput,
  AbsoluteLabel,
  ApplyButton,
  ClockIcon,
  DropdownArrow,
  QuickRangeItem,
  TimeRangeButton,
  TimeRangeDropdownWrap,
  TimeRangeMenu,
  TimeRangeMenuAbsolute,
  TimeRangeMenuBody,
  TimeRangeMenuHeader,
  TimeRangeMenuTab,
} from "./styles";

export interface TimeRangePreset {
  label: string;
  ms: number;
}

export const MINUTE = 60 * 1000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

/** The admin tab's full range list. */
export const TIME_RANGE_PRESETS: TimeRangePreset[] = [
  { label: "Last 5 minutes", ms: 5 * MINUTE },
  { label: "Last 15 minutes", ms: 15 * MINUTE },
  { label: "Last 30 minutes", ms: 30 * MINUTE },
  { label: "Last 1 hour", ms: HOUR },
  { label: "Last 3 hours", ms: 3 * HOUR },
  { label: "Last 6 hours", ms: 6 * HOUR },
  { label: "Last 12 hours", ms: 12 * HOUR },
  { label: "Last 24 hours", ms: 24 * HOUR },
  { label: "Last 2 days", ms: 2 * DAY },
  { label: "Last 7 days", ms: 7 * DAY },
  { label: "Last 30 days", ms: 30 * DAY },
];

/** "YYYY-MM-DDTHH:mm" in LOCAL time — the value shape `datetime-local` wants. */
export function fmtLocalDatetime(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

/** Local "YYYY-MM-DDTHH:mm" back to a UTC ISO string for the API. */
export function toISO(localDatetime: string): string {
  return new Date(localDatetime).toISOString();
}

export function defaultDateRange(spanMs = DAY): { from: string; to: string } {
  const now = new Date();
  return {
    from: fmtLocalDatetime(new Date(now.getTime() - spanMs)),
    to: fmtLocalDatetime(now),
  };
}

export function formatRangeLabel(
  ms: number,
  presets: TimeRangePreset[] = TIME_RANGE_PRESETS
): string {
  const preset = presets.find((p) => p.ms === ms);
  if (preset) return preset.label;
  if (ms < HOUR) return `Last ${Math.round(ms / MINUTE)} min`;
  if (ms < DAY) return `Last ${Math.round(ms / HOUR)} hours`;
  return `Last ${Math.round(ms / DAY)} days`;
}

/** Overridable chrome text, so a translated page can pass its own. */
export type TimeRangeLabels = {
  quick: string;
  absolute: string;
  from: string;
  to: string;
  apply: string;
  custom: string;
};

const DEFAULT_LABELS: TimeRangeLabels = {
  quick: "Quick ranges",
  absolute: "Absolute time range",
  from: "From",
  to: "To",
  apply: "Apply time range",
  custom: "Custom range",
};

export const TimeRangeDropdown: React.FC<{
  selectedMs: number | null;
  from: string;
  to: string;
  onSelectPreset: (ms: number) => void;
  onApplyAbsolute: (from: string, to: string) => void;
  /** Defaults to the full admin list; pass a narrower one to cap the window. */
  presets?: TimeRangePreset[];
  /**
   * Oldest selectable instant, as a local "YYYY-MM-DDTHH:mm". Set on the
   * customer page so the absolute inputs cannot even offer a window the API
   * would reject.
   */
  minDate?: string;
  labels?: Partial<TimeRangeLabels>;
}> = ({
  selectedMs,
  from,
  to,
  onSelectPreset,
  onApplyAbsolute,
  presets = TIME_RANGE_PRESETS,
  minDate,
  labels,
}) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"quick" | "absolute">("quick");
  const [absFrom, setAbsFrom] = useState(from);
  const [absTo, setAbsTo] = useState(to);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Drop undefined entries before merging. A caller reading these out of a
  // translation file passes `t.someKey ?? undefined`, and an untranslated
  // language yields undefined for every one — a plain spread would write those
  // over the English defaults and render the dropdown with blank labels.
  const t = { ...DEFAULT_LABELS } as TimeRangeLabels;
  if (labels) {
    for (const [k, v] of Object.entries(labels)) {
      if (v != null) t[k as keyof TimeRangeLabels] = v;
    }
  }

  useEffect(() => {
    setAbsFrom(from);
    setAbsTo(to);
  }, [from, to]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const label =
    selectedMs != null ? formatRangeLabel(selectedMs, presets) : t.custom;

  return (
    <TimeRangeDropdownWrap ref={dropdownRef}>
      <TimeRangeButton active={open} onClick={() => setOpen(!open)}>
        <ClockIcon>&#128339;</ClockIcon>
        {label}
        <DropdownArrow>&#9662;</DropdownArrow>
      </TimeRangeButton>
      {open && (
        <TimeRangeMenu>
          <TimeRangeMenuHeader>
            <TimeRangeMenuTab
              active={tab === "absolute"}
              onClick={() => setTab("absolute")}
            >
              {t.absolute}
            </TimeRangeMenuTab>
            <TimeRangeMenuTab active={tab === "quick"} onClick={() => setTab("quick")}>
              {t.quick}
            </TimeRangeMenuTab>
          </TimeRangeMenuHeader>
          <TimeRangeMenuBody>
            {tab === "quick" ? (
              presets.map((preset) => (
                <QuickRangeItem
                  key={preset.ms}
                  selected={selectedMs === preset.ms}
                  onClick={() => {
                    onSelectPreset(preset.ms);
                    setOpen(false);
                  }}
                >
                  {preset.label}
                </QuickRangeItem>
              ))
            ) : (
              <TimeRangeMenuAbsolute>
                <div>
                  <AbsoluteLabel>{t.from}</AbsoluteLabel>
                  <AbsoluteInput
                    type="datetime-local"
                    value={absFrom}
                    min={minDate}
                    onChange={(e) => setAbsFrom(e.target.value)}
                  />
                </div>
                <div>
                  <AbsoluteLabel>{t.to}</AbsoluteLabel>
                  <AbsoluteInput
                    type="datetime-local"
                    value={absTo}
                    min={minDate}
                    onChange={(e) => setAbsTo(e.target.value)}
                  />
                </div>
                <ApplyButton
                  onClick={() => {
                    onApplyAbsolute(absFrom, absTo);
                    setOpen(false);
                  }}
                >
                  {t.apply}
                </ApplyButton>
              </TimeRangeMenuAbsolute>
            )}
          </TimeRangeMenuBody>
        </TimeRangeMenu>
      )}
    </TimeRangeDropdownWrap>
  );
};
